import { Box, Typography, Button, Link } from "@mui/material";

/**
 * BulkActionBar — Phase 3 (Design Gap Audit item C) reusable component.
 *
 * Matches the prototype's .bulkbar: a bar that appears above a table once
 * one or more rows are checkbox-selected, showing a "N selected" count,
 * a row of action buttons, and a "Clear" link on the right.
 *
 * Per the implementation instructions: this component does NOT invent any
 * action. It only renders whatever `actions` array the calling page passes
 * in — and each page must only pass actions that are backed by a real,
 * already-existing API call. If a prototype-shown action (e.g. "Add to
 * campaign") has no existing backend support on a given page, the calling
 * page should simply omit that action from the array rather than passing a
 * fake/no-op handler.
 *
 * Props:
 *   count      - number of selected rows (required). Bar renders nothing
 *                (returns null) when count is 0, so callers can mount it
 *                unconditionally.
 *   actions    - array of { label, onClick, disabled? } (required, may be empty)
 *   onClear    - called when "Clear" is clicked (required)
 */
export default function BulkActionBar({ count, actions = [], onClear }) {
  if (!count) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 2,
        py: 1,
        bgcolor: "#eff6ff",
        borderBottom: "1px solid",
        borderColor: "divider",
        flexWrap: "wrap",
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
        {count} selected
      </Typography>

      {actions.map((a) => (
        <Button
          key={a.label}
          size="small"
          variant="outlined"
          onClick={a.onClick}
          disabled={a.disabled}
          sx={{ height: 28, fontSize: 12.5 }}
        >
          {a.label}
        </Button>
      ))}

      <Link
        component="button"
        type="button"
        onClick={onClear}
        underline="hover"
        sx={{ ml: "auto", fontSize: 12.5, color: "text.secondary" }}
      >
        Clear
      </Link>
    </Box>
  );
}
